import React, { useContext, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { TFileExtension, ICustomItemsOfGroups } from 'types';
import Button from 'components/Button/Button';
import Text from 'components/Text/Text';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import { postMediaFile, getFilePathForMediaFilesWithSizeForStyleSettings } from 'backend/additionalInformation';
import { createAppError } from 'utils/helpers';
import { openBrowseFile } from 'utils/openBrowseFile';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import style from './FileSectionForStyleSettings.module.scss';

interface IProps {
	onUploadFile?: (fileId: string, sourceUrl: string) => void;
	onDeleteFile?: () => void;
	onSizeError?: () => void;
	item: Partial<ICustomItemsOfGroups>;
	maxFileSizeMB?: number;
	isDisabledControls?: boolean;
	accept?: TFileExtension[];
}

export const FileSectionForStyleSettings: React.FC<IProps> = React.memo(
	({
		onUploadFile,
		onDeleteFile,
		onSizeError,
		item,
		maxFileSizeMB = 1,
		isDisabledControls = false,
		accept = ['jpg', 'jpeg', 'png', 'mp4', 'pdf', 'zip', 'bin', 'apk', 'html', 'json', 'p12'],
	}) => {
		const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
		const [localFileName, setLocalFileName] = useState('menu.pdf');
		const { t } = useTranslation('error');
		const hasFile = item.fileId || (item.fileId !== null && item.filePath);

		const sendForm = async (files: FileList | null) => {
			if (files) {
				if (!accept.includes(files[0].name.toLowerCase().split('.').pop() as TFileExtension)) {
					addAppError({
						id: uuidv4(),
						message: t('sorryWeDoNotSupportThatTypeOfFile'),
						name: 'noErrorData',
					});
					return;
				}

				const file = files[0];

				if (file.size > 1000 * 1000 * maxFileSizeMB && onSizeError) {
					onSizeError();
					return;
				}

				const formData = new FormData();
				formData.append('file', file, file.name);
				const sourceUrl = URL.createObjectURL(file);
				try {
					showLoader();
					const { data } = await postMediaFile({ data: formData });
					onUploadFile && onUploadFile(data.fileId, sourceUrl);
					setLocalFileName(file.name);
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				} finally {
					hideLoader();
				}
			} else {
				addAppError({
					id: uuidv4(),
					message: t('sorryWeDoNotSupportThatTypeOfFile'),
					name: 'noErrorData',
				});
			}
		};

		const handleUpload = () => {
			openBrowseFile({
				onChoose: sendForm,
				accept: accept.map((extension) => '.' + extension).join(', '),
			});
		};

		const handleShow = async () => {
			const link = document.createElement('a');
			link.target = '_blank';
			if (hasFile && item.sourceUrl) {
				link.href = item.sourceUrl;
			} else if (hasFile && item.filePath) {
				try {
					showLoader();
					const { data } = await getFilePathForMediaFilesWithSizeForStyleSettings(
						item.translationId || '',
						'Original',
					);
					link.href = data.preSignedUrl || '';
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
					return;
				} finally {
					hideLoader();
				}
			}
			link.click();
		};

		return (
			<div className={style.container} data-test="PDFSection">
				{(hasFile || (!onUploadFile && item.filePath)) && <div className={style.fileName}>{localFileName}</div>}
				{onUploadFile && (
					<Button
						theme="secondaryPurple"
						icon={
							<svg className={style.icon}>
								<use xlinkHref={`${breadcrumbs}#${hasFile ? 'reload' : 'uploadPhoto'}`} />
							</svg>
						}
						disabled={isDisabledControls}
						onClick={handleUpload}
						data-test="uploadReloadButton">
						<Text
							translationNameSpace="form"
							translationKey={hasFile ? 'replaceButton' : 'uploadButton'}
							asFragment={true}
						/>
					</Button>
				)}
				<Button
					theme="secondaryPurple"
					icon={
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#viewDocument`} />
						</svg>
					}
					onClick={handleShow}
					disabled={!hasFile}
					data-test="viewDocumentButton">
					<Text translationNameSpace="form" translationKey="viewDocumentButton" asFragment={true} />
				</Button>
				{onDeleteFile && hasFile && (
					<Button
						className={style.deleteButton}
						theme="secondary"
						icon={
							<svg className={style.deleteIcon}>
								<use xlinkHref={`${breadcrumbs}#bin`} />
							</svg>
						}
						disabled={isDisabledControls}
						onClick={onDeleteFile}
						data-test="deleteButton">
						<Text translationNameSpace="form" translationKey="deleteButton" asFragment={true} />
					</Button>
				)}
			</div>
		);
	},
);
